/* eslint-disable object-curly-newline */
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import {
  TilesGridCellImage as CellImage,
  TilesGridCellDecorativeLabel as CellLabel,
  TextWithHighlight,
  TilesGridCellCardWithOverlay,
  TilesGridCellSelectionDecorator,
} from '@bingads-webui-campaign-react/asset-library-components';
import {
  EncodingPlaceholderWithStyle,
  UnverifiedPlaceholderWithStyle,
  FailedPlaceholderWithStyle
} from '@bingads-webui-campaign-react/video-status-placeholder';
import { getTimeDescription, convertTimeDuration } from '@bingads-webui-campaign-react/asset-library-model';

import { getVideoAssetsGridCellStyle } from './styles/video-assets-grid-cell-style';

const VideoAssetsGridCell = ({
  item,
  highlightedText,
  isOverlayEnabled,
  isCellFocused,
  renderCellContent,
  renderOverlayContent,
  selectable,
  selected,
  isMultiSelecting,
  onClick,
  i18n,
  classes,
}) => {
  //
  const [isVideoLoaded, setVideoLoaded] = useState(false);

  const { name, thumbnailUrl, id, createdTime, durationInMS, status } = item || {};
  const timeDesc = useMemo(() => getTimeDescription(createdTime, i18n), [createdTime, i18n]);
  const durationDesc = useMemo(() => convertTimeDuration(durationInMS), [durationInMS]);

  const cellContent = useMemo(() => {
    let defaultContent = null;
    // status order: unverified => draft => active/invalid
    if (status === 'Active') {
      defaultContent = (
        <Fragment>
          <CellImage
            src={thumbnailUrl}
            alt={name || id || ''}
            showSpinner={false}
            onLoaded={setVideoLoaded}
            i18n={i18n}
          />
          {isVideoLoaded && (timeDesc || durationDesc) && (
            <CellLabel
              text={i18n.getString(_TL_('New'))}
              secondaryText={timeDesc}
              iconName="AlertSolid"
              additionaIconName="Video"
              additionalText={durationDesc}
            />
          )}
        </Fragment>
      );
    } else {
      setVideoLoaded(true);

      if (status === 'Unverified') {
        defaultContent = <UnverifiedPlaceholderWithStyle placeholderContent={i18n.getString(_TL_('Downloading...'))} />;
      } else if (status === 'Draft') {
        defaultContent = <EncodingPlaceholderWithStyle placeholderContent={i18n.getString(_TL_('Processing...'))} />;
      } else if (status === 'Failed') {
        defaultContent = <FailedPlaceholderWithStyle placeholderContent={i18n.getString(_TL_('Invalid video'))} />;
      }
    }

    if (renderCellContent) {
      return renderCellContent(defaultContent);
    }
    return defaultContent;
  }, [i18n, id, isVideoLoaded, name, renderCellContent, thumbnailUrl, timeDesc, durationDesc, status]);

  const funcRenderOverlayContent = useCallback(() => {
    if (renderOverlayContent) {
      return renderOverlayContent({ newLabel: timeDesc });
    }
    return null;
  }, [renderOverlayContent, timeDesc]);

  const onCellClick = useCallback(() => {
    if (selectable) {
      onClick({ item, selected });
    }
  }, [item, onClick, selectable, selected]);

  return (
    <div className={classnames('tiles-grid-cell-content', classes.VideoAssetsGridCellContainer)}>
      <TilesGridCellCardWithOverlay
        isCellFocused={isCellFocused}
        isOverlayEnabled={isOverlayEnabled && isVideoLoaded}
        renderOverlayContent={funcRenderOverlayContent}
        customCellCardClass={(isOverlayEnabled && !selected && !isMultiSelecting) ? classes.selectableCardContainerAutoHideCheckbox : classes.selectableCardContainer}
        onClick={onCellClick}
      >
        {selectable ? (
          <TilesGridCellSelectionDecorator item={item} selected={selected} onClick={onClick} ariaLabel={name || i18n.getString(_TL_('Video'))} isFocused={isCellFocused} >
            {cellContent}
          </TilesGridCellSelectionDecorator>
        ) : cellContent}
      </TilesGridCellCardWithOverlay>
      <div className="cell-description">
        <div className="cell-description-name">
          <TextWithHighlight text={name || ''} highlightedText={highlightedText} enableTooltip />
        </div>
        {id && (
          <div className="cell-description-id">
            {`${i18n.getString(_TL_('ID'))} `}
            <TextWithHighlight text={id} highlightedText={highlightedText} />
          </div>
        )}
      </div>
    </div>
  );
};

VideoAssetsGridCell.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
    createdTime: PropTypes.instanceOf(Date),
  }).isRequired,
  highlightedText: PropTypes.string,
  isOverlayEnabled: PropTypes.bool,
  isCellFocused: PropTypes.bool,
  renderCellContent: PropTypes.func,
  renderOverlayContent: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onClick: () => null,
  isMultiSelecting: PropTypes.bool,

  classes: PropTypes.shape({
    VideoAssetsGridCellContainer: PropTypes.string,
    selectableCardContainer: PropTypes.string,
    selectableCardContainerAutoHideCheckbox: PropTypes.string,
  }).isRequired,
};

VideoAssetsGridCell.defaultProps = {
  highlightedText: undefined,
  isOverlayEnabled: false,
  isCellFocused: false,
  renderCellContent: null,
  renderOverlayContent: null,
  selectable: false,
  selected: false,
  onClick: undefined,
  isMultiSelecting: false,
};

export const AssetLibraryVideoAssetsGridCellBase
  = withDefaultStyles(VideoAssetsGridCell, getVideoAssetsGridCellStyle);
